import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { companyService, CompanySettings } from '../services/companyService';
import { useAuth } from './AuthContext';

interface CompanyContextType {
  company: CompanySettings | undefined;
  isLoading: boolean;
  error: any;
  refreshCompany: () => void;
  updateCompanyTitle: (name: string) => void;
  updateCompanyFavicon: (faviconUrl: string) => void;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

interface CompanyProviderProps {
  children: ReactNode;
}

export const CompanyProvider: React.FC<CompanyProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const {
    data: company,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['company-settings'],
    queryFn: companyService.getCompanySettings,
    enabled: isAuthenticated && !!user,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10 // 10 minutes (previously cacheTime)
  });

  const refreshCompany = () => {
    refetch();
  };

  const updateCompanyTitle = (name: string) => {
    companyService.updateDocumentTitle(name);
    // Also update the cached data
    queryClient.setQueryData(['company-settings'], (oldData: CompanySettings | undefined) => {
      if (oldData) {
        return { ...oldData, name };
      }
      return oldData;
    });
  };

  const updateCompanyFavicon = (faviconUrl: string) => {
    companyService.updateFavicon(faviconUrl);
    // Also update the cached data
    queryClient.setQueryData(['company-settings'], (oldData: CompanySettings | undefined) => {
      if (oldData) {
        return { ...oldData, favicon: faviconUrl };
      }
      return oldData;
    });
  };

  // Set initial document title and favicon on mount
  useEffect(() => {
    if (company?.name) {
      companyService.updateDocumentTitle(company.name);
    }
    if (company?.favicon) {
      companyService.updateFavicon(company.favicon);
    }
  }, [company]);

  const value: CompanyContextType = {
    company,
    isLoading,
    error,
    refreshCompany,
    updateCompanyTitle,
    updateCompanyFavicon
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = (): CompanyContextType => {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

// Hook to get formatted company information
export const useCompanyInfo = () => {
  const { company } = useCompany();

  if (!company) {
    return {
      displayName: 'Mi Empresa',
      logoUrl: '',
      faviconUrl: '',
      contactInfo: '',
      logoShape: 'square',
      logoZoom: 100
    };
  }

  return companyService.formatCompanyInfo(company);
};